import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { lazy, Suspense } from 'react';
import { Button } from '@leadsnavi/ui-kit';
import { Paperclip } from 'lucide-react';
import { useMailEditorStore } from '@/stores/mailEditorStore';
import { editEmailPost } from '@/services/swagger/easourcing/WriteService';
import { taskApproveGet } from '@/services/swagger/gator/WriteService';
import { emailOperatePost } from '@/services/swagger/gator/CommsFlowService';
import { toast } from '@leadsnavi/ui-kit';
import { ModalComponentProps } from '@/components/moda-lInvoker';
import { EmailStatus } from '@/types/email-unified.model';
import type { AttachmentsModel } from '@/stores/mailEditorStore';
import { MailEditorRef } from '../index';
import PageLoading from '@/common/base/PageLoading';
import SubjectInput from '../SubjectInput';
import mixpanel from 'mixpanel-browser';

// 懒加载邮件编辑器组件
const LazyMailEditor = lazy(() => import('../index'));

export interface CampaignMailEditorData {
  subject: string;
  content: string;
  attachments: AttachmentsModel[];
}

/**
 * 全局营销信编辑对话框组件
 * 通过全局 store 管理状态，无需在各个组件中透传参数
 * 使用全局弹窗系统，解决右键菜单定位问题
 * 提供取消和更新操作，没有发送功能
 */
export const GlobalCampaignMailEditorDialog: React.FC<ModalComponentProps> = ({ onCancel }) => {
  const { campaignEditData, campaignDialogOpen, closeCampaignDialog } = useMailEditorStore();
  const editorRef = useRef<MailEditorRef>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [subject, setSubject] = useState('');

  // 计算初始值，使用 useMemo 稳定引用
  const initialValues = useMemo(() => {
    if (!campaignEditData) {
      return {
        subject: '',
        content: '',
        attachments: [] as AttachmentsModel[],
      };
    }
    return {
      subject: campaignEditData.subject || '',
      content: campaignEditData.content || '',
      attachments: campaignEditData.attachments || [],
    };
  }, [campaignEditData]);

  // 同步主题变化
  useEffect(() => {
    if (initialValues.subject) {
      setSubject(initialValues.subject);
    }
  }, [initialValues.subject]);

  /**
   * 编辑器创建后的回调处理
   */
  const handleEditCreated = useCallback(
    (_ref: MailEditorRef) => {
      // 不能直接拿到实例，因为编辑器是懒加载的，所以需要使用setTimeout
      setTimeout(() => {
        if (editorRef.current) {
          editorRef.current.setContent(initialValues.content);
          editorRef.current.setAttachments(initialValues.attachments);
          setSubject(initialValues.subject);
        } else {
          console.error('editorRef.current is null', editorRef.current);
        }
      }, 200);
    },
    [initialValues.content, initialValues.attachments, initialValues.subject]
  );

  /**
   * 主题变更处理
   */
  const handleSubjectChange = (newSubject: string) => {
    setSubject(newSubject);
  };

  /**
   * 获取当前附件数量
   */
  const getAttachmentCount = () => {
    if (editorRef.current) {
      return editorRef.current.getAttachments().length;
    }
    return initialValues.attachments.length;
  };

  /**
   * 滚动到附件区域
   */
  const handleAttachmentsClick = () => {
    const el = document.querySelector('.scrollBox');
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  };

  /**
   * 处理取消操作
   */
  const handleCancel = () => {
    closeCampaignDialog();
    onCancel?.();
  };

  /**
   * 处理更新操作
   */
  const handleUpdate = async () => {
    if (!editorRef.current || !campaignEditData) return;

    try {
      setIsUpdating(true);

      const currentContent = editorRef.current.getContent();
      const currentAttachments = editorRef.current.getAttachments();

      await editEmailPost({
        msgId: campaignEditData.msgId,
        contactId: campaignEditData.contactId,
        contentId: campaignEditData.contentId,
        subject: subject,
        content: currentContent,
        manualSave: 0,
        attachments: currentAttachments.map(attachment => ({
          fileName: attachment.fileName,
          fileSize: attachment.fileSize,
          type: attachment.type === 'MAIL_PICS' ? 1 : 0,
          downloadUrl: attachment.downloadUrl,
          expireTime: attachment.expireTime ?? 0,
          nosKey: attachment.nosKey,
        })),
      })
        .then(() => {
          // 只有当邮件状态为 pending 时才调用 Approve
          if (campaignEditData.emailStatus === EmailStatus.GeneratingPending) {
            mixpanel.track('homepage_tasks_approve_click');

            // 如果有 contentId，使用 taskApproveGet（从 task-card 流程进入）
            // 如果没有 contentId，使用 emailOperatePost（从 chat-email-card 进入）
            if (campaignEditData.contentId) {
              taskApproveGet({
                taskId: campaignEditData.contentId,
              })
                .then(() => {
                  toast.success('Email updated successfully');
                  closeCampaignDialog();
                  onCancel?.();
                })
                .catch(err => {
                  console.error('Email updated failed', err);
                  toast.error('Failed to approve email');
                });
            } else {
              // 从 chat-email-card 进入，使用 emailOperatePost
              emailOperatePost({
                contactEmailMsgId: campaignEditData.msgId,
                contactId: campaignEditData.contactId,
                emailStatus: campaignEditData.emailStatus,
                operateType: 'APPROVE',
              })
                .then(response => {
                  if (response.success) {
                    toast.success('Email updated successfully');
                    closeCampaignDialog();
                    onCancel?.();
                  } else {
                    throw new Error(response.message || 'Failed to approve email');
                  }
                })
                .catch(err => {
                  console.error('Email updated failed', err);
                  toast.error(err instanceof Error ? err.message : 'Failed to approve email');
                });
            }
          } else {
            // 非 pending 状态，直接关闭弹窗
            toast.success('Email updated successfully');
            closeCampaignDialog();
            onCancel?.();
          }
        })
        .catch(err => {
          console.error('Email updated failed', err);
        });
    } catch (error) {
      console.error('Failed to update campaign email:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  /**
   * 自定义工具栏插件 - 提供取消和更新按钮
   */
  const customToolbarPlugin = (
    <div className="flex items-center justify-end gap-3">
      <Button variant="outline" onClick={handleCancel} disabled={isUpdating} className="min-w-24">
        Cancel
      </Button>
      <Button onClick={handleUpdate} disabled={isUpdating} className="min-w-24">
        {isUpdating ? 'Updating...' : 'Update'}
      </Button>
    </div>
  );

  // 如果没有编辑数据，不渲染任何内容
  if (!campaignDialogOpen || !campaignEditData) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* 背景遮罩 */}
      <div className="absolute inset-0 bg-[#00000080]" />

      {/* 弹窗内容 */}
      <div className="relative w-[90vw] h-[85vh] min-w-[864px] min-h-[648px] max-w-[95vw] max-h-[95vh] bg-white rounded-[8px] shadow-2xl flex flex-col">
        {/* 编辑器内容区域 - 包含底部工具栏 */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {/* 头部区域 */}
          <div className="flex-shrink-0 pt-3 pl-1 pr-4 gap-0">
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <SubjectInput
                  value={subject}
                  onChange={handleSubjectChange}
                  maxLength={256}
                  placeholder="Please enter the Subject"
                />
              </div>
              <div
                onClick={handleAttachmentsClick}
                className="flex rounded-[8px] p-2 items-center gap-1 cursor-pointer hover:bg-[#E5E5E5]"
              >
                <Paperclip size={16} />
                <span className="text-xs text-500 font-medium text-[#262626]">({getAttachmentCount()})</span>
              </div>
            </div>
          </div>

          {/* 编辑器区域 */}
          <div className="flex-1 flex flex-col h-[calc(100%-48px)]">
            <Suspense fallback={<PageLoading />}>
              <LazyMailEditor
                ref={editorRef}
                uniqueClass="global-campaign-mail-editor"
                attachedStickyBottom={true}
                showBottomToolbar={false}
                onEditCreated={handleEditCreated}
                customToolbarPlugin={customToolbarPlugin}
              />
            </Suspense>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalCampaignMailEditorDialog;
